import { FC, useEffect, useState, MouseEvent } from "react";
import { styled } from "@mui/material/styles";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockResetIcon from "@mui/icons-material/LockReset";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { CustomButton } from "../../../../components";
import { Colors } from "../../../../configs";
import { AddUsersDrawer } from "../../../../components/admin/add-new-user-drawer/add-new-user-drawer";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { getAllUsers, setAccessCodeAction, setNfcCardAction, lockUserAccountAction, resetUnlockAttemptsAction, revokeUserSessionAction } from "../../../../redux/actions/authAction";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import useNfcReader from "../../../../hooks/useNfcReader";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export const UserManagements: FC = () => {
  const dispatch = useAppDispatch();
  const {
    allUsers,
    allUsersLoading,
    allUsersError,
    signupData,
    updateData,
    setRoleData,
  } = useAppSelector((state) => state.auth);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  const [accessCodeDialogOpen, setAccessCodeDialogOpen] = useState(false);
  const [newAccessCode, setNewAccessCode] = useState("");
  const [confirmAccessCode, setConfirmAccessCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [accessCodeLoading, setAccessCodeLoading] = useState(false);

  const [nfcDialogOpen, setNfcDialogOpen] = useState(false);
  const [scannedNfcCard, setScannedNfcCard] = useState<string | null>(null);
  const [nfcCardLoading, setNfcCardLoading] = useState(false);

  const rawNfcScan = useNfcReader();
  const snackBar = useSnackbarContext();

  useEffect(() => {
    dispatch(getAllUsers());
  }, []);

  useEffect(() => {
    dispatch(getAllUsers());
  }, [signupData]);

  useEffect(() => {
    dispatch(getAllUsers());
  }, [updateData]);

  useEffect(() => {
    dispatch(getAllUsers());
  }, [setRoleData]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>, user: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOnDrawerClose = () => {
    setOpenDrawer(false);
    setSelectedUser(null);
  };

  const handleOpenAccessCodeDialog = () => {
    handleClose();
    setNewAccessCode("");
    setConfirmAccessCode("");
    setShowCode(false);
    setAccessCodeDialogOpen(true);
  };

  const handleSaveAccessCode = async () => {
    if (!newAccessCode.trim()) return;
    if (newAccessCode !== confirmAccessCode) {
      snackBar.showSnackbar("Access codes do not match.", "error");
      return;
    }
    setAccessCodeLoading(true);
    try {
      await dispatch(
        setAccessCodeAction({ userId: selectedUser.id, accessCode: newAccessCode })
      ).unwrap();
      snackBar.showSnackbar("Access code updated successfully.", "success");
      setAccessCodeDialogOpen(false);
    } catch (err: any) {
      snackBar.showSnackbar(err ?? "Failed to set access code.", "error");
    } finally {
      setAccessCodeLoading(false);
    }
  };

  const handleRevokeSession = async () => {
    if (!selectedUser) return;
    handleClose();
    try {
      await dispatch(revokeUserSessionAction(selectedUser.id)).unwrap();
      snackBar.showSnackbar(
        `Session revoked for ${selectedUser.firstName} ${selectedUser.lastName}. They will be logged out on their next action.`,
        "success"
      );
    } catch (err: any) {
      snackBar.showSnackbar(err ?? "Failed to revoke session.", "error");
    }
  };

  const handleResetUnlockAttempts = async () => {
    if (!selectedUser) return;
    handleClose();
    try {
      await dispatch(resetUnlockAttemptsAction(selectedUser.id)).unwrap();
      snackBar.showSnackbar(
        `Unlock attempts reset for ${selectedUser.firstName} ${selectedUser.lastName}.`,
        "success"
      );
      dispatch(getAllUsers());
    } catch (err: any) {
      snackBar.showSnackbar(err ?? "Failed to reset unlock attempts.", "error");
    }
  };

  const handleToggleLock = async () => {
    if (!selectedUser) return;
    handleClose();
    const isCurrentlyLocked = selectedUser.isLocked === true;
    try {
      if (!isCurrentlyLocked) {
        await dispatch(lockUserAccountAction(selectedUser.id)).unwrap();
        snackBar.showSnackbar(
          `${selectedUser.firstName} ${selectedUser.lastName}'s account has been locked.`,
          "success"
        );
      }
      dispatch(getAllUsers());
    } catch (err: any) {
      snackBar.showSnackbar(err ?? "Failed to update account lock status.", "error");
    }
  };

  const handleOpenNfcDialog = () => {
    handleClose();
    setScannedNfcCard(null);
    setNfcDialogOpen(true);
  };

  useEffect(() => {
    if (!nfcDialogOpen || !rawNfcScan) return;
    setScannedNfcCard(rawNfcScan);
  }, [rawNfcScan, nfcDialogOpen]);

  const handleSaveNfcCard = async () => {
    if (!scannedNfcCard) return;
    setNfcCardLoading(true);
    try {
      await dispatch(
        setNfcCardAction({ userId: selectedUser.id, nfcCardId: scannedNfcCard })
      ).unwrap();
      snackBar.showSnackbar("NFC card assigned successfully.", "success");
      setNfcDialogOpen(false);
    } catch (err: any) {
      snackBar.showSnackbar(err ?? "Failed to assign NFC card.", "error");
    } finally {
      setNfcCardLoading(false);
    }
  };

  return (
    <AdminLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "3rem",
          height: "57.5rem",
          marginTop: "4.357rem",
          paddingInline: "5rem",
          width: "100%",
          maxHeight: "57.5rem",
        }}
      >
        <AddUsersDrawer
          open={openDrawer}
          toggleDrawer={handleOnDrawerClose}
          populateFor={
            selectedUser
              ? {
                  id: selectedUser.id,
                  firstName: selectedUser.firstName,
                  lastName: selectedUser.lastName,
                  email: selectedUser.email,
                  password: "",
                  role: selectedUser.role?.name ?? "",
                  roleId: selectedUser.role?.id ?? "",
                }
              : undefined
          }
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ color: Colors.TEXT_GRAY_DARK }}>
            User Management
          </Typography>
          <CustomButton
            sx={{ width: "11rem", height: "3rem" }}
            onClick={() => {
              setOpenDrawer(true);
              setSelectedUser(null);
            }}
          >
            Add New
          </CustomButton>
        </Box>

        <Box
          sx={{
            maxHeight: "51rem",
            width: "100%",
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: "0.5rem" },
            "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
            "&::-webkit-scrollbar-thumb": { background: "#888" },
            "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
          }}
        >
          <Table sx={{ width: "100%" }} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell>First Name</StyledTableCell>
                <StyledTableCell>Last Name</StyledTableCell>
                <StyledTableCell>Email</StyledTableCell>
                <StyledTableCell>Gender</StyledTableCell>
                <StyledTableCell>Role</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allUsersLoading && (
                <StyledTableRow>
                  <StyledTableCell colSpan={7} sx={{ textAlign: "center" }}>
                    <CircularProgress size={24} color="inherit" />
                  </StyledTableCell>
                </StyledTableRow>
              )}

              {!allUsersLoading &&
                allUsers?.map((user) => (
                  <StyledTableRow key={user.id}>
                    <StyledTableCell>{user.firstName}</StyledTableCell>
                    <StyledTableCell>{user.lastName}</StyledTableCell>
                    <StyledTableCell>{user.email}</StyledTableCell>
                    <StyledTableCell>{user.gender ?? "-"}</StyledTableCell>
                    <StyledTableCell>{user.role?.name ?? "-"}</StyledTableCell>
                    <StyledTableCell>
                      {user.isLocked ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#b91c1c",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                          }}
                        >
                          <LockIcon sx={{ fontSize: "0.9rem" }} />
                          {(user.failedUnlockAttempts ?? 0) >= 5
                            ? "Suspended"
                            : "Locked"}
                          {(user.failedUnlockAttempts ?? 0) > 0 && (
                            <span style={{ color: "#ef4444", marginLeft: "0.2rem" }}>
                              ({user.failedUnlockAttempts}/5)
                            </span>
                          )}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Active
                        </Typography>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      <IconButton onClick={(e) => handleClick(e, user)}>
                        <MoreHorizIcon
                          sx={{ fontSize: "2rem" }}
                          color="primary"
                        />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}

              {!allUsersLoading && allUsers.length === 0 && (
                <StyledTableRow>
                  <StyledTableCell colSpan={7} sx={{ textAlign: "center" }}>
                    No users found.
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          MenuListProps={{ "aria-labelledby": "basic-button" }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              setOpenDrawer(true);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit User</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenAccessCodeDialog}>
            <ListItemIcon>
              <VpnKeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Set Access Code</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenNfcDialog}>
            <ListItemIcon>
              <CreditCardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Assign NFC Card</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleToggleLock}>
            <ListItemIcon>
              {selectedUser?.isLocked ? (
                <LockOpenIcon fontSize="small" />
              ) : (
                <LockIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {selectedUser?.isLocked ? "Unlock Account" : "Lock Account"}
            </ListItemText>
          </MenuItem>
          {selectedUser?.isLocked && (selectedUser?.failedUnlockAttempts ?? 0) > 0 && (
            <MenuItem onClick={handleResetUnlockAttempts}>
              <ListItemIcon>
                <LockResetIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Reset Unlock Attempts</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={handleRevokeSession}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Revoke Session</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => snackBar.showSnackbar("Delete coming soon!", "info")}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete User</ListItemText>
          </MenuItem>
        </Menu>

        {/* NFC Card Dialog */}
        <Dialog
          open={nfcDialogOpen}
          onClose={() => setNfcDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: "0.75rem", p: "0.5rem", minWidth: "22rem" } }}
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>
            Assign NFC Card
            {selectedUser && (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: "0.25rem" }}>
                {selectedUser.firstName} {selectedUser.lastName}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1rem", pt: "0.5rem !important" }}>
            {scannedNfcCard ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" }}>
                <CreditCardIcon sx={{ fontSize: "3rem", color: Colors.PRIMARY }} />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Card detected
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontFamily: "monospace", letterSpacing: "0.1em", color: Colors.TEXT_GRAY_DARK }}
                >
                  {"•".repeat(Math.max(0, scannedNfcCard.length - 4)) + scannedNfcCard.slice(-4)}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem", py: "1rem" }}>
                <CreditCardIcon sx={{ fontSize: "3rem", color: "text.disabled" }} />
                <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
                  Scan the NFC card now…
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: "1.5rem", pb: "1rem" }}>
            <CustomButton
              onClick={() => setNfcDialogOpen(false)}
              sx={{ height: "2.4rem", backgroundColor: "#e0e0e0", color: Colors.TEXT_GRAY_DARK }}
            >
              Cancel
            </CustomButton>
            <CustomButton
              onClick={handleSaveNfcCard}
              disabled={!scannedNfcCard || nfcCardLoading}
              sx={{ height: "2.4rem" }}
            >
              {nfcCardLoading ? <CircularProgress size={18} color="inherit" /> : "Assign"}
            </CustomButton>
          </DialogActions>
        </Dialog>

        {/* Access Code Dialog */}
        <Dialog
          open={accessCodeDialogOpen}
          onClose={() => setAccessCodeDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: "0.75rem", p: "0.5rem", minWidth: "22rem" } }}
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>
            Set Access Code
            {selectedUser && (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: "0.25rem" }}>
                {selectedUser.firstName} {selectedUser.lastName}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1rem", pt: "0.5rem !important" }}>
            <TextField
              label="New Access Code"
              type={showCode ? "text" : "password"}
              value={newAccessCode}
              onChange={(e) => setNewAccessCode(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowCode((v) => !v)}>
                      {showCode ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm Access Code"
              type={showCode ? "text" : "password"}
              value={confirmAccessCode}
              onChange={(e) => setConfirmAccessCode(e.target.value)}
              fullWidth
              size="small"
              error={confirmAccessCode.length > 0 && newAccessCode !== confirmAccessCode}
              helperText={
                confirmAccessCode.length > 0 && newAccessCode !== confirmAccessCode
                  ? "Codes do not match"
                  : ""
              }
            />
          </DialogContent>
          <DialogActions sx={{ px: "1.5rem", pb: "1rem" }}>
            <CustomButton
              onClick={() => setAccessCodeDialogOpen(false)}
              sx={{ height: "2.4rem", backgroundColor: "#e0e0e0", color: Colors.TEXT_GRAY_DARK }}
            >
              Cancel
            </CustomButton>
            <CustomButton
              onClick={handleSaveAccessCode}
              disabled={
                !newAccessCode.trim() ||
                newAccessCode !== confirmAccessCode ||
                accessCodeLoading
              }
              sx={{ height: "2.4rem" }}
            >
              {accessCodeLoading ? <CircularProgress size={18} color="inherit" /> : "Save"}
            </CustomButton>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
